MxContextService.$inject = ['$q', 'mx.shell.EventService', 'mx.shell.RoutingService', 'mx.internationalization'];

class MxContext {
	constructor(injector, options) {
		Object.defineProperty(this, 'data', {
			get: this._getData,
			set: this._setData,
		});
		Object.defineProperty(this, 'async', {
			writable: false,
			value: options.async,
		});
		Object.defineProperty(this, 'cleanOnRouteChange', {
			writable: false,
			value: options.cleanOnRouteChange,
		});
		if (options.async) {
			Object.defineProperty(this, 'deferred', {
				writable: true,
				value: injector.$q.defer(),
			});
		}
		this._data = options.data;
	}
	_getData() {
		return this._data;
	}
	_setData(value) {
		this._data = value;
	}
	_reset(injector) {
		if (this.async) {
			this.deferred = injector.$q.defer();
		}
		this.data = null;
	}
	publish(injector, data) {
		if (data === null) {
			this._reset(injector);
		} else if (this.async) {
			this.deferred.resolve(data);
		} else {
			this.data = data;
		}
	}
	read() {
		return this.async ? this.deferred.promise : this.data;
	}
}

class MxSelectedObjectsContext extends MxContext {
	constructor(injector) {
		super(injector, new MxContextOptions([], false, true));
	}
	publish(injector, data) {
		var objects = data || [];

		this.data = Array.isArray(objects) ? objects : [objects];
		injector.eventService.dispatchEvent(mx.shell.EventCodes.ObjectSelectionChanged, this.data);
	}
}

class MxActionsContextContext extends MxContext {
	constructor(injector) {
		super(injector, new MxContextOptions(null, false, true));
	}
	publish(injector, data, append) {
		if (!append && this.data === data) {
			return;
		}
		if (!data) {
			this.data = null;
		} else if (append) {
			var currentContext = this.data;

			if (currentContext) {
				var newContext = new mx.shell.ActionsContext();

				newContext.concat(currentContext);
				newContext.concat(data);
				this.data = newContext;
			} else {
				this.data = data;
			}
		} else {
			this.data = data;
		}
		injector.eventService.dispatchEvent(mx.shell.EventCodes.ActionsContextChanged, this.data);
	}
}

function MxContextOptions(data, async, cleanOnRouteChange) {
	Object.defineProperty(this, 'data', {
		writable: true,
		value: data,
	});
	Object.defineProperty(this, 'async', {
		writable: true,
		value: Boolean(async),
	});
	Object.defineProperty(this, 'cleanOnRouteChange', {
		writable: true,
		value: Boolean(cleanOnRouteChange),
	});
}

/**
 * @ngdoc service
 * @name mx.shell.ContextService
 * @module mx.shell
 *
 * @description
 * Provides logic for obtaining and managing current context of the Application.
 *
 * @property {object[]} selectedObjects - Gets or sets list of the business objects selected on that moment of time.
 * @property {object} actionsContext - Gets or sets ActionContext defined for the current View*
 * */
function MxContextService($q, eventService, routingService, internationalization) {
	var contexts = {};
	var globalVal = {};

	var injector = {
		$q: $q,
		eventService: eventService,
	};

	eventService.subscribeGlobal(mx.shell.EventCodes.RouteActivated, function (event, route) {
		for (var contextType in contexts) {
			if (contexts.hasOwnProperty(contextType)) {
				var context = contexts[contextType];

				if (context.cleanOnRouteChange && !routingService.isCurrent(route.name, route.parameters)) {
					context.publish(injector, null);
				}
			}
		}
	});

	var result = {
		register: register,
		registerContext: registerContext,
		publish: publish,
		read: read,
		val: val,
		valAsync: valAsync,
	};

	function val(name, data) {
		// Simple schema
		if (!name) {
			throw new Error(internationalization.get('shell.errors.value_name_not_provided'));
		}
		if (typeof data === 'undefined') {
			return globalVal[name];
		}
		globalVal[name] = data;
	}

	function valAsync(name, data) {
		return _val(name, data, true);
	}

	function _val(name, data, async) {
		name = formatValName(name);
		verifyContext(name, data, async);
		if (typeof data === 'undefined') {
			return read(name);
		}
		publish(name, data);
	}

	function formatValName(name) {
		if (!name) {
			throw new Error(internationalization.get('shell.errors.value_name_not_provided'));
		}
		return 'mx.shell.ValueContext.' + name;
	}

	function verifyContext(name, value, async) {
		var context = contexts[name];

		if (!context) {
			register(name, new MxContextOptions(value, async));
		}
	}

	/**
	 * @ngdoc method
	 * @name mx.shell.ContextService#register
	 *
	 * @description
	 * Registers new application context.
	 *
	 * @param {MxContextTypes} type - Type of the context.
	 * @param {MxContextOptions} options - Context options
	 */
	function register(type, options) {
		registerContext(type, new MxContext(injector, options));
	}

	/**
	 * @ngdoc method
	 * @name mx.shell.ContextService#registerContext
	 *
	 * @description
	 * Registers new application context.
	 *
	 * @param {MxContextTypes | string} type - Type of the context.
	 * @param {MxContext} context - Context
	 */
	function registerContext(type, context) {
		if (!type) {
			throw new Error(internationalization.get('shell.errors.registerContext_not_provided'));
		}
		if (contexts.hasOwnProperty(type)) {
			throw new Error(internationalization.get('shell.errors.registerContext_type_already_registered'));
		}
		contexts[type] = context;
	}

	/**
	 * @ngdoc method
	 * @name mx.shell.ContextService#getContext
	 *
	 * @description
	 * Gets application context by type.
	 *
	 * @param {MxContextTypes} type - Type of the context.
	 */
	function getContext(type) {
		var context = contexts[type];

		if (!context) {
			throw new Error(internationalization.get('shell.errors.context_with_type') + ' ' + type + ' ' + internationalization.get('shell.errors.not_found'));
		}
		return context;
	}

	function publish(type, data, append) {
		getContext(type).publish(injector, data, append);
	}

	function read(type) {
		return getContext(type).read();
	}

	// System contexts

	result.registerContext(MxContextTypes.SelectedObjects, new MxSelectedObjectsContext(injector));
	result.registerContext(MxContextTypes.ActionsContext, new MxActionsContextContext(injector));

	Object.defineProperty(result, 'selectedObjects', {
		get: getSelectedObjects,
		set: setSelectedObjects,
	});
	Object.defineProperty(result, 'actionsContext', {
		get: getActionsContext,
		set: setActionsContext,
	});

	result.extendActionsContext = function (context) {
		publish(MxContextTypes.ActionsContext, context, true);
	};

	function getSelectedObjects() {
		return read(MxContextTypes.SelectedObjects);
	}

	function setSelectedObjects(objects) {
		publish(MxContextTypes.SelectedObjects, objects);
	}

	function getActionsContext() {
		return read(MxContextTypes.ActionsContext);
	}

	function setActionsContext(context) {
		publish(MxContextTypes.ActionsContext, context);
	}

	return result;
}

const MxContextTypes = {
	SelectedObjects: 'mx.shell.SelectedObjects',
	ActionsContext: 'mx.shell.ActionsContext',
};

export { MxContextService, MxContextOptions, MxContextTypes };
